import React, { useCallback, useEffect, useState } from 'react';
import { track } from '@amplitude/analytics-browser';
import { Box, Card, Checkbox, Input, Text } from '@nimbus-ds/components';
import { ExclamationCircleIcon } from '@nimbus-ds/icons';
import { MoneyObjectInterface } from 'commons/types';
import { parseDecimalAR } from 'commons/utils';
import { useTransferValidation } from 'domains/Transfer/hooks/useTransferValidation';

export interface AmountCardProps {
  value: MoneyObjectInterface;
  maxAvailableValue: MoneyObjectInterface;
  setValue: (value: MoneyObjectInterface) => void;
  setError: (error: boolean) => void;
  disabled?: boolean;
}

function AmountCard({
  value,
  maxAvailableValue,
  setValue,
  setError,
  disabled,
}: Readonly<AmountCardProps>): JSX.Element {
  const { validate, t } = useTransferValidation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isChecked, setIsChecked] = useState(false);

  const validateAmount = useCallback(
    (amount: number): void => {
      const message = validate(amount, maxAvailableValue?.value ?? 0);
      setErrorMessage(message);
      setError(message !== null);
    },
    [validate, maxAvailableValue?.value, setError],
  );

  useEffect(() => {
    if (value?.value) {
      validateAmount(value.value);
    }
  }, [value?.value, validateAmount]);

  const inputOnChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const amountNumber = parseDecimalAR(event.target.value);
      validateAmount(amountNumber);
      setValue({ value: amountNumber, currency: value.currency });
      setIsChecked(false);
    },
    [validateAmount, setValue, value.currency],
  );

  const checkboxOnChangeHandler = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setIsChecked(event.target.checked);

      if (event.target.checked) {
        track('nuvempago_transfer_all_balance_select');
        const availableAmount = maxAvailableValue?.value ?? 0;
        setValue({
          value: availableAmount,
          currency: maxAvailableValue?.currency,
        });
        validateAmount(availableAmount);
      }
    },
    [maxAvailableValue, setValue, validateAmount],
  );

  return (
    <Card padding="none" data-testid="WithdrawAmountForm-AmountCard">
      <Card.Body padding="none">
        <Box display="grid" margin="4" gap="5">
          <Box display="grid" gap="2">
            <Input
              type="number"
              inputMode="decimal"
              name="withdrawAmount"
              data-testid="withdrawalAmountInput"
              min={0}
              placeholder="0,00"
              step="0.01"
              autoFocus={!disabled}
              appearance={errorMessage ? 'danger' : 'neutral'}
              append={
                <Text color="neutral-textDisabled">
                  {value.currency === 'MXN' ? 'MXN' : '$'}
                </Text>
              }
              onChange={inputOnChangeHandler}
              disabled={disabled}
              value={value?.value ? value.value : ''}
            />
            {errorMessage && (
              <Text
                color="danger-textLow"
                fontSize="caption"
                lineHeight="caption"
              >
                <Box
                  display="inline-flex"
                  as="span"
                  gap="1"
                  justifyContent="center"
                >
                  <ExclamationCircleIcon width="14px" height="14px" />
                  {errorMessage}
                </Box>
              </Text>
            )}
          </Box>
          <Checkbox
            name="transferAvailableAmount"
            data-testid="transferAvailableAmount"
            label={t('transferTotalAvailable')}
            onChange={checkboxOnChangeHandler}
            disabled={disabled}
            checked={isChecked}
          />
        </Box>
      </Card.Body>
    </Card>
  );
}

import AmountCardSkeleton from './AmountCardSkeleton';

AmountCard.Skeleton = AmountCardSkeleton;

export default AmountCard;
